/**
 * /settings — account, plan, credit meter and preferences.
 *
 * Layout, copy and styles verbatim from the monolith's `view === "settings"` block,
 * with one section added since: the account name is editable here (Batch 6 §4).
 *
 * §1 also lists /settings/profile, /settings/billing and /settings/security. The
 * prototype has no such screens — Settings is one page whose sections cover all
 * three. Rather than invent three empty pages, those paths redirect here (see
 * router.tsx), which keeps the URLs valid for later without adding UI the design
 * does not have.
 *
 * WHY THE NAME IS EDITED HERE AND NOT IN A DIALOG OF ITS OWN. The picture opens a
 * dialog because choosing a file, previewing it and approving it is a task with steps;
 * a name is one field, and a dialog around one field is a click and a focus trap in
 * exchange for nothing. It sits under the header that renders the name, so the change
 * is visible in place the moment it saves.
 *
 * IT IS SHOWN AS A VALUE UNTIL YOU ASK TO CHANGE IT (Batch 7 §4). The section used to be a
 * permanently-open input with a standalone "Save name" button parked beside it — a form
 * presented for a value nobody was editing, next to a button disabled almost all of the
 * time. §4 asks for that button's removal: the name reads as text with an Edit control
 * beside it, and the field, its Save and its Cancel appear when Edit is pressed. Escape
 * cancels, focus moves into the field on open and back to Edit on close, and a save that
 * fails leaves the editor open holding what was typed. The SAVE ITSELF IS UNCHANGED — same
 * `updateProfile`, same validation, same toast — because §4 also says not to build a second
 * profile system, and a disclosure around an existing save is not one.
 *
 * IT SAVES THROUGH THE ACCOUNT THAT ALREADY EXISTS — `authStore.updateProfile`, the
 * same action the profile picture uses, whose validation lives in `authApi.nameError`
 * so the form's refusal and the API's are one rule. There is no second profile store,
 * no second user record and no local copy of the name: every surface that shows it
 * (the sidebar, the mini player, "Welcome back", the licence agreements) reads the one
 * in authStore and follows the save without being wired to this page.
 *
 * SIMULATED, with ONE EXCEPTION. The plan card reflects client-side entitlements and no
 * billing exists. When the frontend runs without a configured backend (`USE_MOCK_API`), the
 * name is not persisted — see `authApi.updateProfile`; a reload loses it exactly as it loses
 * the session, and the page says so where the name is edited. When a backend IS configured,
 * `authApi.updateProfile` PATCHes `/users/me`, whose server route writes the account record
 * through the active persistence adapter (`server/src/routes/auth.ts`, `server/src/db/*`).
 * The vocal style, export format and email toggle are not persisted either, and say so in
 * `stores/preferencesStore.ts`.
 *
 * "Appearance" IS persisted and IS fully functional, because a theme that forgot itself on
 * reload would be a broken feature rather than a mock of one. It writes `Lyrixa.theme` and
 * needs no backend to be real, which is exactly why it is the one preference that works
 * end to end.
 */
import { useEffect, useRef, useState } from 'react';
import { BadgeCheck, Camera, ChevronRight, Pencil } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import Avatar from '../components/account/Avatar';
import { TextField } from '../components/forms';
import { Btn } from '../components/ui';
import { USE_MOCK_API } from '../config';
import { MAX_NAME_LENGTH, nameError } from '../api/authApi';
import { VOCALS } from '../data/music';
import { FRONTEND_PRODUCT } from '../data/products';
import { useCredits, useEntitlements } from '../hooks';
import { selectProfileEmail, selectProfileName, useAuthStore } from '../stores/authStore';
import {
  NO_DEFAULT_VOCAL,
  usePreferencesStore,
  type ExportFormat,
  type ThemeName,
} from '../stores/preferencesStore';
import { useUiStore } from '../stores/uiStore';
import { c } from '../styles/theme';
import { paths } from './paths';

/** Shared style for the small inline selects in the preferences list. */
const INLINE_SELECT: React.CSSProperties = {
  fontSize: 12,
  width: 140,
  background: c.surface1,
  border: `1px solid ${c.border}`,
  borderRadius: 6,
  color: c.text1,
  padding: '5px 8px',
};

/**
 * A section label — "Account", "Preferences".
 *
 * Extracted when the second one appeared: the two were the same six declarations
 * written twice, which is how a page ends up with two heading sizes.
 *
 * text2 rather than the text3 the lone "Preferences" heading used. It is a heading
 * people read to find their way down the page, and text3 clears 4.5:1 against nothing
 * — see the table in styles/theme.ts. Same size, same weight, same margins.
 */
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h2
      style={{
        fontSize: 12,
        color: c.text2,
        margin: '0 0 8px',
        fontWeight: 400,
        fontFamily: 'Inter',
      }}
    >
      {children}
    </h2>
  );
}

/** One row in the preferences list. */
function PreferenceRow({
  label,
  htmlFor,
  children,
  last,
}: {
  label: string;
  htmlFor?: string;
  children: React.ReactNode;
  last?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 0',
        borderBottom: last ? undefined : `1px solid ${c.border}`,
      }}
    >
      <label htmlFor={htmlFor} style={{ fontSize: 13 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

/*
  `initials()` used to live here, as did the 40px circle it filled. Both moved to
  components/account/Avatar.tsx when the picture became something a user can change:
  the circle is now a control that opens the chooser, and the fallback belongs with
  the component that draws it rather than with the one page that happened to be the
  only avatar surface. Same pixels — see the note in Avatar.
*/

export default function Settings() {
  const navigate = useNavigate();

  const profileName = useAuthStore(selectProfileName);
  const profileEmail = useAuthStore(selectProfileEmail);
  const authProvider = useAuthStore((s) => s.user?.authProvider ?? 'password');
  const isAdmin = useAuthStore((s) => s.user?.isAdmin === true);
  const appAccess = useAuthStore((s) => s.appAccess);
  const updateProfile = useAuthStore((s) => s.updateProfile);

  const { commercial, unlimited } = useEntitlements();
  const {
    isUnlimited,
    resetsAt,
    total,
    unlimitedExpiresAt,
    used,
  } = useCredits();

  const defaultVocal = usePreferencesStore((s) => s.defaultVocal);
  const setDefaultVocal = usePreferencesStore((s) => s.setDefaultVocal);
  const exportFormat = usePreferencesStore((s) => s.exportFormat);
  const setExportFormat = usePreferencesStore((s) => s.setExportFormat);
  const emailUpdates = usePreferencesStore((s) => s.emailUpdates);
  const setEmailUpdates = usePreferencesStore((s) => s.setEmailUpdates);
  const theme = usePreferencesStore((s) => s.theme);
  const setTheme = usePreferencesStore((s) => s.setTheme);

  const showToast = useUiStore((s) => s.showToast);
  const setChangePasswordOpen = useUiStore((s) => s.setChangePasswordOpen);
  const setDeleteAccountOpen = useUiStore((s) => s.setDeleteAccountOpen);
  const setProfilePictureOpen = useUiStore((s) => s.setProfilePictureOpen);

  const canEditLockedPreferences = isAdmin || appAccess === 'granted';

  /*
    THE NAME FORM'S STATE, and why the field is local while the name is not.

    A controlled field has to hold what is being typed, and what is being typed is not
    yet the account's name — writing each keystroke to the store would rename the user in
    the sidebar letter by letter and make Cancel impossible. So the draft is local and the
    store is written once, on Save. Same division the picture dialog makes between its
    preview and `updateProfile`.
  */
  const [draftName, setDraftName] = useState(profileName);
  const [nameSaving, setNameSaving] = useState(false);
  /** The API's refusal, when a save is rejected for a reason the form did not catch. */
  const [saveError, setSaveError] = useState<string | null>(null);
  /*
    WHETHER THE NAME IS BEING EDITED AT ALL — Batch 7 §4, and the only genuinely new
    state here.

    The section used to be a permanently-open field with a standalone "Save name" button
    beside it, which is the control §4 asks to remove. The name is now shown as the value it
    is, with an edit icon; pressing that opens the field, and the field's own Save and Cancel
    replace the button that used to sit outside it. Nothing about where the name GOES has
    changed — `updateProfile` below is untouched.
  */
  const [editingName, setEditingName] = useState(false);
  /** The input, so opening the editor puts the caret in it rather than leaving it adrift. */
  const nameInputRef = useRef<HTMLInputElement>(null);
  /** The edit icon, so closing the editor hands focus back to what opened it. */
  const editButtonRef = useRef<HTMLButtonElement>(null);

  /*
    Follows the stored name when it changes underneath the form.

    Needed because the name has another writer: signing in as a different account replaces
    the user record while this page is mounted, and without this the field would still hold
    the previous person's name with Save enabled — one click from writing it back. Keyed on
    `profileName` only, so it does not fight the user's typing.

    It closes the editor too, for the same reason: an open field holding the previous
    account's name is the state that made this dangerous, and the new occupant did not ask
    to rename themselves.
  */
  useEffect(() => {
    setDraftName(profileName);
    setSaveError(null);
    setEditingName(false);
  }, [profileName]);

  /*
    The caret goes into the field when the editor opens.

    An edit control that reveals an input and leaves focus on itself makes the user click
    twice for one action, and gives a keyboard user no indication that anything appeared.
    `autoFocus` would do it in one line and is avoided deliberately: it also fires on the
    initial mount if the editor ever defaults to open, which is how a settings page ends up
    stealing focus from the top of itself on arrival.
  */
  useEffect(() => {
    if (!editingName) return;
    const field = nameInputRef.current;
    if (!field) return;
    field.focus();
    // Caret at the end rather than selecting the whole value: the common edit is fixing a
    // typo in a name that is mostly right, and a full selection means the first keystroke
    // deletes it.
    field.setSelectionRange(field.value.length, field.value.length);
  }, [editingName]);

  /** Why the draft cannot be saved, or null. One rule, shared with the API. */
  const draftProblem = nameError(draftName);
  const nameChanged = draftName.trim() !== profileName;

  /** Opens the editor on the stored name. */
  function handleStartEditingName() {
    if (!canEditLockedPreferences) return;
    // From the store, not from whatever the draft last held: an abandoned edit must not
    // still be sitting in the field the next time it opens.
    setDraftName(profileName);
    setSaveError(null);
    setEditingName(true);
  }

  /**
   * Closes the editor without saving, and puts the typed name back.
   *
   * REVERT, not merely close: the draft returns to the stored name, so reopening starts from
   * the account's name rather than from an edit the user explicitly abandoned. Focus goes
   * back to the edit icon, which is where it came from and the only remaining control.
   */
  function handleCancelEditingName() {
    setDraftName(profileName);
    setSaveError(null);
    setEditingName(false);
    // After the state change, so the button exists to receive it.
    requestAnimationFrame(() => editButtonRef.current?.focus());
  }

  async function handleSaveName() {
    // Guarded as well as disabled: Enter in the field reaches this too, and a form that
    // only checks in the button's `disabled` is a form that submits on Enter regardless.
    if (draftProblem || !nameChanged || nameSaving) return;
    setNameSaving(true);
    setSaveError(null);
    try {
      await updateProfile({ name: draftName });
      showToast('Name updated');
      /*
        CLOSES ONLY ON SUCCESS. A save that failed leaves the editor open holding what was
        typed, beside the API's reason — closing it would discard the user's text and leave
        an error message about a field that is no longer on screen. Focus stays in the field
        for the same reason: the next move is to fix the name, not to reopen the editor.
      */
      setEditingName(false);
      requestAnimationFrame(() => editButtonRef.current?.focus());
    } catch (err) {
      // The API's own sentence — it says which rule was broken, which "Could not save"
      // does not. The fallback covers a failure that is not an ApiError at all.
      setSaveError(err instanceof Error ? err.message : 'Could not update your name');
    } finally {
      setNameSaving(false);
    }
  }

  function handleOpenTopUp() {
    navigate(paths.topup);
  }

  const finiteTotal = Math.max(0, total ?? 0);
  const usedPercent = finiteTotal > 0
    ? Math.min(100, Math.max(0, Math.round((used / finiteTotal) * 100)))
    : 0;
  const unlimitedTermActive =
    unlimited &&
    isUnlimited &&
    (!unlimitedExpiresAt || Date.parse(unlimitedExpiresAt) > Date.now());

  return (
    <div>
      {/* Profile header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          paddingBottom: 14,
          marginBottom: 14,
          borderBottom: `1px solid ${c.border}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          {/* The picture IS the control that changes it — no extra button, which is
              why the brief's "clicking the current profile picture opens an upload
              interface" needed no new row in this header. The small camera badge is
              what says so before you hover, since a photo does not look pressable.

              Labelled rather than left aria-hidden as the plain circle was: it is a
              button now, so it needs a name, and "Change profile picture" is the
              action rather than the account (whose name is rendered beside it). */}
          <button
            type="button"
            className="lyx-avatar-button"
            onClick={() => setProfilePictureOpen(true)}
            aria-label="Change profile picture"
            style={{ position: 'relative' }}
          >
            <Avatar />
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                right: -2,
                bottom: -2,
                width: 16,
                height: 16,
                borderRadius: '50%',
                background: c.surface3,
                // Rings the badge in the page colour so it reads as sitting on top of
                // the circle rather than being part of the picture.
                border: `1.5px solid ${c.bg}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Camera size={9} color={c.text2} />
            </span>
          </button>
          <div style={{ minWidth: 0 }}>
            <h1
              style={{
                fontWeight: 500,
                fontSize: 14,
                margin: 0,
                fontFamily: 'Inter',
                color: c.text1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {profileName}
            </h1>
            {/* text2, not the text3 this line used to be: it is the address the account
                signs in with, which is information rather than decoration, and text3
                clears 4.5:1 against none of the app's surfaces. Same size and position. */}
            <p
              style={{
                fontSize: 12,
                color: c.text2,
                margin: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {profileEmail}
            </p>
          </div>
        </div>
      </div>

      {/* ---- Account: the name, shown as a value with an edit icon (Batch 7 §4) ----
          Under the header that renders the result. Deliberately not a card: the page already
          has three bordered boxes below it, and a fourth around a single input is the
          overcrowding the brief warns against.

          ─────────────────────────────────────────────────────────────────────────────────
          WHAT CHANGED AND WHY, since §4 names the control it wants gone: "Remove the
          separate 'Save Name' button… name displays normally, edit icon appears
          inside/alongside the name field, clicking edit enables editing".

          Before, this was an input that was always open with a Save button parked beside it
          permanently — so the page presented an editable form for a value nobody was
          editing, and a button that was disabled almost all of the time. The name now reads
          as what it is, with a pencil beside it; the field and its Save appear when the
          pencil is pressed and go away again afterwards.

          NOTHING ABOUT WHERE THE NAME GOES CHANGED. `handleSaveName` above is the same
          function calling the same `authStore.updateProfile` → `authApi.updateProfile`
          (PATCH /users/me) it always did. This is a disclosure around an existing save, not
          a second profile path — which is the one thing §4 prohibits.

          A real <form> still, so Enter saves. That is the gesture people use in a one-field
          form, and without a form element the key does nothing — which reads as the field
          being broken rather than as a design decision.
          ───────────────────────────────────────────────────────────────────────────────── */}
      <SectionLabel>Account</SectionLabel>
      {editingName ? (
        <form
          className="lyx-settings-name"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSaveName();
          }}
          onKeyDown={(e) => {
            /*
              Escape cancels, which is what it does in every other dismissable thing in the
              app (the modals, the Studio's panels, the nav drawer). On the form rather than
              on the input so it also works from the two buttons, and `nameSaving` is
              excluded because a request is already in flight — abandoning the UI while the
              account is being written is how the screen and the store disagree.
            */
            if (e.key === 'Escape' && !nameSaving) {
              e.preventDefault();
              handleCancelEditingName();
            }
          }}
          style={{ marginBottom: 6 }}
        >
          {/* Wrapped rather than given the class directly: TextField takes no className, and
              adding one to a component used at a dozen sites for this page's layout is a
              wider change than this needs. The div is what grows and shrinks. */}
          <div className="lyx-settings-name-field">
            <TextField
              id="lyx-account-name"
              label="Your name"
              value={draftName}
              // The caret goes in here when the editor opens — see the effect above.
              inputRef={nameInputRef}
              onChange={(value) => {
                setDraftName(value);
                // Cleared on edit, not on save: an error left on screen while the field it
                // describes has changed is about text that is no longer there.
                setSaveError(null);
              }}
              placeholder="Your name"
            />
          </div>
          {/* The two controls, together and inside the form — which is the difference from
              the standalone button that was removed. `lyx-settings-name-actions` keeps them
              side by side once the row stacks on a phone. */}
          <div className="lyx-settings-name-actions">
            <Btn
              type="submit"
              /*
                Disabled while unchanged as well as while invalid, so Save is only live when
                pressing it would do something. `nameChanged` compares the TRIMMED draft,
                which is what would be stored — otherwise adding a trailing space would
                enable a Save that stores the name it already had.
              */
              disabled={Boolean(draftProblem) || !nameChanged || nameSaving}
              aria-describedby={draftProblem ? 'lyx-account-name-problem' : undefined}
              style={{ flexShrink: 0 }}
            >
              {nameSaving ? 'Saving…' : 'Save'}
            </Btn>
            <Btn
              type="button"
              variant="secondary"
              onClick={handleCancelEditingName}
              // Not while a save is in flight: the request cannot be recalled, so a Cancel
              // that appeared to stop it would be a lie about what the server is doing.
              disabled={nameSaving}
              style={{ flexShrink: 0 }}
            >
              Cancel
            </Btn>
          </div>
        </form>
      ) : (
        <div className="lyx-settings-name" style={{ marginBottom: 6 }}>
          {/*
            The name as a value. `lyx-settings-name-field` so it occupies the same slot the
            input does and the row does not resize when the editor opens.

            The label above it is a <p> rather than a <label>: there is no form control here
            to label, and a <label> with nothing to point at is a hollow one. The edit
            button's own name carries the association instead — "Edit your name".
          */}
          <div className="lyx-settings-name-field">
            <p style={{ fontSize: 12, color: c.text2, margin: '0 0 6px' }}>Your name</p>
            <p
              style={{
                fontSize: 13,
                color: c.text1,
                margin: 0,
                // The input's own vertical padding, so the value sits where the field's text
                // sits and nothing moves when the editor opens.
                padding: '9px 0',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {profileName}
            </p>
          </div>
          <div className="lyx-settings-name-actions">
            <Btn
              variant="secondary"
              onClick={handleStartEditingName}
              disabled={!canEditLockedPreferences}
              // Focus comes back here when the editor closes, saved or cancelled.
              buttonRef={editButtonRef}
              style={{ flexShrink: 0 }}
            >
              {/*
                An icon AND the word, rather than the bare pencil §4's "edit icon" would
                allow. A glyph alone on a settings page is a guess — this is the only edit
                control on the page and there is room for four characters beside it. The icon
                is `aria-hidden` by lucide's default, so the accessible name is the word.
              */}
              <Pencil size={13} />
              Edit
            </Btn>
          </div>
        </div>
      )}

      {/*
        WHY THIS SLOT IS THE SAME ONE FOR BOTH MESSAGES. `draftProblem` is what the form
        knows before sending; `saveError` is what the API said after. They cannot both be
        true — the API is only reached once the form is satisfied — so one line carries
        whichever applies, rather than the page growing a row per failure mode.

        The validation line is NOT role="alert": it appears while you type, and a live
        region that announces on every keystroke is unusable. It is tied to the button with
        aria-describedby instead, which is what explains a disabled control. The API's
        refusal IS an alert, because it arrives without the user doing anything further.
      */}
      {draftProblem ? (
        <p
          id="lyx-account-name-problem"
          style={{ fontSize: 11.5, color: c.text2, margin: '0 0 18px' }}
        >
          {draftProblem}
        </p>
      ) : saveError ? (
        <p role="alert" style={{ fontSize: 11.5, color: c.danger, margin: '0 0 18px' }}>
          {saveError}
        </p>
      ) : (
        <p style={{ fontSize: 11.5, color: c.text2, margin: '0 0 4px' }}>
          {canEditLockedPreferences ? (
            <>
              Shown on your tracks, your playlists and your licence agreements. Up to{' '}
              {MAX_NAME_LENGTH} characters.
            </>
          ) : (
            <>
              Shown on your tracks, your playlists and your licence agreements. Name changes are
              available after your purchase is verified.
            </>
          )}
        </p>
      )}

      {USE_MOCK_API && (
        /*
          ───────────────────────────────────────────────────────────────────────────────
          WHAT A SAVE ACTUALLY DOES, said to the user rather than only in this file's header.

          Batch 7 §4: "Do NOT fake successful persistence. If backend persistence is
          unavailable, the UI must remain honest about that." In mock mode it is unavailable —
          `authApi.updateProfile` echoes the patch back, so the new name lives in `authStore`
          and nowhere else, and a reload loses it exactly as it loses the session. Every
          surface that renders the name follows the change immediately, which is precisely
          what makes the gap invisible without this line: the app looks like it saved.

          Below the message slot rather than inside it, so it is on screen in every state — a
          note about persistence that vanished while a validation message showed would be
          missing at the moment somebody is looking hardest at this section. Same sentence
          pattern and the same size as the profile picture dialog's note, which says the same
          thing about the same endpoint.
          ─────────────────────────────────────────────────────────────────────────────── */
        <p style={{ fontSize: 10.5, color: c.text2, lineHeight: 1.5, margin: '0 0 18px' }}>
          Your name is held in this browser tab only — there is no account storage connected yet, so
          signing out or reloading restores the original.
        </p>
      )}

      {/* ---- Plan, licence and credits ----
          The three cards below were already here and are UNCHANGED — same copy, same
          colours, same destinations, same entitlement reads. What is new is the label above
          them: three bordered boxes in a row with nothing saying what they have in common
          is the page reading as a stack rather than as sections, which is what §4's "easy
          to understand" is about. One line of type, no new controls. */}
      <SectionLabel>Plan &amp; billing</SectionLabel>

      {/* Plan */}
      <div
        style={{
          background: c.accentSoft,
          borderRadius: 8,
          padding: '10px 14px',
          marginBottom: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <div>
          <p style={{ fontSize: 13, fontWeight: 500, margin: 0, color: c.accent }}>
            {FRONTEND_PRODUCT.name}
          </p>
          <p style={{ fontSize: 11, margin: 0, color: c.accent }}>
            Lifetime access{unlimited && ' · Unlimited'}
          </p>
        </div>
        <Btn variant="secondary" onClick={() => navigate(paths.upgrade)} style={{ flexShrink: 0 }}>
          Products &amp; Billing
        </Btn>
      </div>

      {/* Commercial licence */}
      <div
        style={{
          border: `1px solid ${c.border}`,
          borderRadius: 10,
          padding: '12px 14px',
          marginBottom: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          background: c.surface1,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <BadgeCheck size={16} color={commercial ? c.success : c.text3} />
          <div style={{ minWidth: 0 }}>
            <p style={{ fontSize: 13, fontWeight: 500, margin: 0 }}>Commercial License</p>
            {/* text2: this line is the licence's STATUS, which is the one thing the card
                exists to tell you. It was the page's worst text3 call — 11px at 3.1:1. */}
            <p style={{ fontSize: 11, color: c.text2, margin: 0 }}>
              {commercial ? 'Active — lifetime commercial rights' : 'Not active'}
            </p>
          </div>
        </div>
        <Btn variant="secondary" onClick={() => navigate(paths.licenses)} style={{ flexShrink: 0 }}>
          {commercial ? 'View' : 'Learn more'}
        </Btn>
      </div>

      {/* Credit meter */}
      <div
        style={{
          background: c.accentSoft,
          border: `1px solid ${c.accentBorder}`,
          borderRadius: 10,
          padding: '12px 14px',
          marginBottom: 20,
        }}
      >
        <>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 10,
                flexWrap: 'wrap',
                gap: 6,
              }}
            >
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: c.accent,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                Credits used
              </span>
              <span style={{ fontSize: 13, color: c.accent, fontFamily: 'IBM Plex Mono' }}>
                {usedPercent}%
              </span>
            </div>
            <div
              aria-hidden="true"
              style={{
                width: '100%',
                height: 8,
                borderRadius: 999,
                overflow: 'hidden',
                background: c.surface1,
                border: `1px solid ${c.accentBorder}`,
                marginBottom: 10,
              }}
            >
              <div
                style={{
                  width: `${usedPercent}%`,
                  minWidth: usedPercent > 0 ? 6 : 0,
                  height: '100%',
                  borderRadius: 999,
                  background: c.accent,
                }}
              />
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              <span style={{ fontSize: 12, color: c.text2 }}>
                {used} of {finiteTotal} credits used
                {unlimitedTermActive && (
                  <>
                    {' · Monthly allowance resets every month'}
                    {resetsAt
                      ? ` · Next reset ${new Date(resetsAt).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}`
                      : ''}
                  </>
                )}
              </span>
              <Btn variant="secondary" onClick={handleOpenTopUp} style={{ flexShrink: 0 }}>
                Top up credits
              </Btn>
            </div>
          </>
      </div>

      {/* Preferences */}
      <SectionLabel>Preferences</SectionLabel>

      {/*
        THEME, first in the list because it is the only preference on this page that does
        something visible the moment it changes — and it changes this page along with
        everything else, which is its own confirmation that it worked.

        A <select> rather than a switch: the same control as the two below it, so the row
        keeps the section's rhythm, and it stays a select if a third option is ever added
        (following the OS, say) where a two-state switch would have to be rebuilt. This
        one IS persisted, unlike its neighbours — see stores/preferencesStore.ts.
      */}
      <PreferenceRow label="Appearance" htmlFor="pref-theme">
        <select
          id="pref-theme"
          value={theme}
          onChange={(e) => setTheme(e.target.value as ThemeName)}
          style={INLINE_SELECT}
        >
          <option value="dark">Dark</option>
          <option value="light">Light</option>
        </select>
      </PreferenceRow>

      <PreferenceRow label="Default vocal style" htmlFor="pref-vocal">
        <select
          id="pref-vocal"
          value={defaultVocal}
          onChange={(e) => setDefaultVocal(e.target.value)}
          disabled={!canEditLockedPreferences}
          style={INLINE_SELECT}
        >
          <option value={NO_DEFAULT_VOCAL}>{NO_DEFAULT_VOCAL}</option>
          {VOCALS.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
      </PreferenceRow>

      {/* Live control, despite `exportFormat` having no reader yet — see
          stores/preferencesStore.ts before deleting it as dead code. */}
      <PreferenceRow label="Export format" htmlFor="pref-format">
        <select
          id="pref-format"
          value={exportFormat}
          onChange={(e) => setExportFormat(e.target.value as ExportFormat)}
          disabled={!canEditLockedPreferences}
          style={INLINE_SELECT}
        >
          <option value="MP3">MP3</option>
          <option value="WAV">WAV</option>
        </select>
      </PreferenceRow>

      {/* `last`, so the list ends here. The prop existed and was never passed, which is
          why the hairline under the final preference ran straight into Delete account and
          made a destructive action look like a fourth setting. */}
      <PreferenceRow label="Email updates" htmlFor="pref-email" last>
        <input
          id="pref-email"
          type="checkbox"
          checked={emailUpdates}
          onChange={(e) => setEmailUpdates(e.target.checked)}
          style={{ width: 'auto' }}
        />
      </PreferenceRow>

      {authProvider === 'password' && (
        <button
          type="button"
          onClick={() => setChangePasswordOpen(true)}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 0',
            marginTop: 14,
            borderTop: `1px solid ${c.border}`,
            cursor: 'pointer',
            background: 'none',
            borderLeft: 'none',
            borderRight: 'none',
            borderBottom: 'none',
            font: 'inherit',
            textAlign: 'left',
          }}
        >
          <span style={{ fontSize: 13, color: c.text1 }}>Change password</span>
          <ChevronRight size={14} color={c.text3} />
        </button>
      )}

      {/* Was a click-only <div>, so it could not be reached by keyboard. Same
          pixels, now a real button.

          Set apart from the preferences above it rather than continuing their rhythm: it
          is the one irreversible thing on the page, and a row that looks like the three
          toggles above it is a row you can reach by momentum. The top rule and the space
          are the whole change — no confirmation is added here, because the dialog it opens
          already asks for one. */}
      <button
        type="button"
        onClick={() => setDeleteAccountOpen(true)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 0',
          borderTop: `1px solid ${c.border}`,
          cursor: 'pointer',
          background: 'none',
          borderLeft: 'none',
          borderRight: 'none',
          borderBottom: 'none',
          font: 'inherit',
          textAlign: 'left',
        }}
      >
        <span style={{ fontSize: 13, color: c.danger }}>Delete account</span>
        {/* text3 is correct here and stays: a chevron beside the words "Delete account",
            which say the same thing. Nothing depends on the glyph. */}
        <ChevronRight size={14} color={c.text3} />
      </button>
    </div>
  );
}
